import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { plansApi, type Plan, ApiError } from "@/lib/api";

function formatPrice(price: string): string {
  const n = parseFloat(price);
  return `R ${n.toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export default function PricingPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    plansApi
      .list()
      .then((res) => setPlans(res.data))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Failed to load plans"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background-100">
      {/* Header */}
      <div className="flex items-center justify-between px-6 sm:px-10 py-6 max-w-6xl mx-auto">
        <img src="/logo.svg" alt="SupplyGuard" className="h-10 w-auto" />
        <div className="flex items-center gap-4">
          <Link to="/login" className="text-foreground-600 text-sm font-medium hover:text-foreground-800">Sign in</Link>
          <Link to="/register" className="h-10 px-4 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg flex items-center transition-colors">
            Get Started
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-20 pt-6 text-center">
        <h1 className="text-foreground-900 text-3xl sm:text-4xl font-bold mb-3">Simple, transparent pricing</h1>
        <p className="text-foreground-500 text-base max-w-xl mx-auto mb-12">
          Every plan includes a 14-day free trial. No credit card required to start.
        </p>

        {loading && <p className="text-foreground-400 text-sm">Loading plans…</p>}
        {error && <p className="text-accent-600 text-sm">{error}</p>}

        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan, i) => {
              const isMiddle = i === 1;
              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl p-8 text-left flex flex-col ${
                    isMiddle
                      ? "bg-primary-600 text-white shadow-xl scale-105"
                      : "bg-white border border-background-200"
                  }`}
                >
                  {isMiddle && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary-500 text-white text-[11px] font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  )}
                  <p className={`text-sm font-semibold mb-1 ${isMiddle ? "text-primary-100" : "text-foreground-500"}`}>{plan.name}</p>
                  <p className={`text-3xl font-bold mb-1 ${isMiddle ? "text-white" : "text-foreground-900"}`}>
                    {formatPrice(plan.price)}
                    <span className={`text-sm font-medium ${isMiddle ? "text-primary-200" : "text-foreground-400"}`}> /month</span>
                  </p>

                  <ul className="mt-6 space-y-3 flex-1">
                    <li className="flex items-center gap-2 text-sm">
                      <i className={`ri-check-line ${isMiddle ? "text-secondary-300" : "text-secondary-500"}`}></i>
                      <span className={isMiddle ? "text-primary-50" : "text-foreground-700"}>
                        {plan.max_contracts ? `Up to ${plan.max_contracts} active contracts` : "Unlimited contracts"}
                      </span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <i className={`ri-check-line ${isMiddle ? "text-secondary-300" : "text-secondary-500"}`}></i>
                      <span className={isMiddle ? "text-primary-50" : "text-foreground-700"}>
                        {plan.max_users ? `Up to ${plan.max_users} users` : "Unlimited users"}
                      </span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <i className={`ri-check-line ${isMiddle ? "text-secondary-300" : "text-secondary-500"}`}></i>
                      <span className={isMiddle ? "text-primary-50" : "text-foreground-700"}>Alerts, reports & audit trail</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <i className={`${plan.has_workflow_engine ? "ri-check-line" : "ri-close-line"} ${isMiddle ? "text-secondary-300" : plan.has_workflow_engine ? "text-secondary-500" : "text-foreground-300"}`}></i>
                      <span className={isMiddle ? "text-primary-50" : plan.has_workflow_engine ? "text-foreground-700" : "text-foreground-400"}>
                        Renewal Workflow, SLA Matrix &amp; Supplier Reviews
                      </span>
                    </li>
                  </ul>

                  <Link
                    to="/register"
                    className={`mt-8 h-11 rounded-lg flex items-center justify-center text-sm font-semibold transition-colors ${
                      isMiddle
                        ? "bg-white text-primary-700 hover:bg-primary-50"
                        : "bg-primary-600 text-white hover:bg-primary-700"
                    }`}
                  >
                    Start free trial
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
